import { change, onSnapshot } from './helpers'

import LogItem from '@/models/LogItem'

import { db, auth, serverTimestamp } from '@/firebase'
import {
  LogContext,
  LogState,
  LogItemCategoryTypeInterface,
  LogAddInterface,
  LogUpdateInterface,
  LogDeleteInterface,
  RootState,
} from '@/types/store'
import { id } from '@/types/models'
import JFileContainer from '@/models/JFileContainer'
import { GetterTree } from 'vuex'

export default {
  namespaced: true,
  state: (): LogState => ({
    data: [],
    unbinds: [],
  }),
  getters: {
    all: (state: LogState) => state.data,
    find: (state: LogState) => (gardenId: id) =>
      state.data.find((li: LogItem) => li.id === gardenId),
    inGarden: (state: LogState) => (gardenId: id) =>
      state.data.filter((li: LogItem) => li.gardenId === gardenId),
    plantLog: (state: LogState) => (gardenId: id, plantId: id) =>
      state.data.filter((z) =>
        z.refs.includes(`gardens/${gardenId}/plants/${plantId}`)
      ),

    categories: (state: LogState, getters) => (gardenId: id) =>
      [
        ...new Set(
          getters['inGarden'](gardenId).map((l: LogItem) => l.category)
        ),
      ],
    types: (state: LogState, getters) => (gardenId: id) =>
      [...new Set(getters['inGarden'](gardenId).map((l: LogItem) => l.type))],
    categoryTypes: (state: LogState, getters) => (gardenId: id) =>
      getters['categories'](gardenId).reduce(
        (acc: LogItemCategoryTypeInterface, category: string) => {
          acc[category] = [
            ...new Set(
              state.data
                .filter((l) => l.category === category)
                .map((l) => l.type)
            ),
          ] // getters['getCategoryTypes'](category)
          return acc
        },
        {} as LogItemCategoryTypeInterface
      ),
  } as GetterTree<LogState, RootState>,
  mutations: {
    change,
    clear(state: LogState) {
      state.data = []
    },

    addUnbind(state: LogState, val: () => void) {
      state.unbinds.push(val)
    },
  },
  actions: {
    bind(context: LogContext, gardenId: id) {
      // unsubscribe can be called to stop listening for changes
      const unsubscribe = db.onCollectionSnapshot(
        db.query(
          db
            .collection('gardens', gardenId, 'log')
            .withConverter(LogItem.firestoreConverter(gardenId))
        ),
        (ref) => onSnapshot(context, ref)
      )

      // save unbind function to store
      context.commit('addUnbind', unsubscribe)
    },
    unbind(context: LogContext) {
      // unsubscribe from firestore
      context.state.unbinds.forEach((fn) => fn())

      // clear from vuex store
      context.commit('clear')

      // TODO: unbind children based on collections() instead of root
    },
    add({ dispatch }: LogContext, { gardenId, item, images }: LogAddInterface) {
      // let logDocRef: DocumentReference
      item.isNew = true

      return new Promise((resolve, reject) => {
        return db
          .addDoc(
            db
              .collection('gardens', gardenId, 'log')
              .withConverter(LogItem.firestoreConverter(gardenId)),
            item
          )
          .then((logDocRef) => {
            return Promise.all(
              !images || images.length < 1
                ? []
                : images.map((file: JFileContainer) =>
                    dispatch(
                      'gardens/images/add',
                      {
                        gardenId,
                        file,
                        refs: [`gardens/${gardenId}/log/${logDocRef.id}`],
                      },
                      { root: true }
                    )
                  )
            ).then(() => resolve(logDocRef))
          })

          .catch((err: Error) => reject(err))
      })
    },
    update(context: LogContext, { gardenId, id, updates }: LogUpdateInterface) {
      const docRef = db.doc('gardens', gardenId, 'log', id)

      updates.updatedBy = auth.currentUser()?.uid
      updates.updatedAt = serverTimestamp()

      return db.updateDoc(docRef, updates)
    },
    delete(context: LogContext, { gardenId, id }: LogDeleteInterface) {
      return db.deleteDoc(db.doc('gardens', gardenId, 'log', id))
    },
  },
}
